import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  MagnifyingGlass, 
  Warning, 
  ShieldCheck, 
  FirstAid, 
  Dog, 
  Baby,
  CheckCircle,
  XCircle,
  ArrowRight,
  FileText,
  Bell
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HypersensitivityCenter() {
  const [ingredients, setIngredients] = useState([]);
  const [stateRegistries, setStateRegistries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRiskLevel, setSelectedRiskLevel] = useState('all');
  const [selectedState, setSelectedState] = useState('Pennsylvania');
  const [profile, setProfile] = useState(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const fetchIngredients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hypersensitivity/ingredients`);
      setIngredients(data);
    } catch (error) {
      console.error('Error fetching ingredients:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStateRegistries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/state-registries`);
      setStateRegistries(data);
    } catch (error) {
      console.error('Error fetching state registries:', error);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/hypersensitivity/profile`, { withCredentials: true });
      setProfile(data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching profile:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchIngredients();
    fetchStateRegistries();
    if (user) {
      fetchProfile();
    }
  }, [user, fetchIngredients, fetchStateRegistries, fetchProfile]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const profileData = {
      has_asthma: formData.get('has_asthma') === 'on',
      has_allergies: formData.get('has_allergies') === 'on',
      has_chemical_sensitivity: formData.get('has_chemical_sensitivity') === 'on',
      has_skin_conditions: formData.get('has_skin_conditions') === 'on',
      has_pets: formData.get('has_pets') === 'on',
      pet_types: formData.get('pet_types') ? formData.get('pet_types').split(',').map(t => t.trim()) : [],
      has_children: formData.get('has_children') === 'on',
      children_ages: formData.get('children_ages') ? formData.get('children_ages').split(',').map(t => t.trim()) : [],
      specific_sensitivities: formData.get('specific_sensitivities') ? formData.get('specific_sensitivities').split(',').map(t => t.trim()) : [],
      notes: formData.get('notes') || ''
    };

    try {
      const { data } = await axios.post(`${API}/hypersensitivity/profile`, profileData, { withCredentials: true });
      setProfile(data);
      setShowProfileForm(false);
      toast.success('Sensitivity profile saved successfully');
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const filteredIngredients = ingredients.filter(ingredient => {
    const matchesSearch = ingredient.ingredient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ingredient.common_products.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRisk = selectedRiskLevel === 'all' || ingredient.risk_level === selectedRiskLevel;
    return matchesSearch && matchesRisk;
  });

  const selectedRegistry = stateRegistries.find(r => r.state === selectedState);

  const riskLevelColors = {
    low: 'bg-green-100 text-green-800 border-green-300',
    moderate: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    high: 'bg-red-100 text-red-800 border-red-300'
  };

      <Navbar />
  return (
    <div className="min-h-screen bg-background_light">
      {/* Hero Section */}
      <div className="bg-primary text-text_light py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={48} weight="duotone" />
            <h1 className="text-5xl sm:text-6xl tracking-tight font-semibold">
              Hypersensitivity Resource Center
            </h1>
          </div>
          <p className="text-xl leading-relaxed text-secondary max-w-4xl">
            Protecting individuals with allergies, asthma, chemical sensitivities, and vulnerable populations. 
            Know your rights, understand ingredients, and access state registry information.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* State Registry Section - Featured */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Bell size={32} weight="duotone" className="text-accent" />
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">State Pesticide Registries</h2>
          </div>
          
          <div className="bg-white border border-borders rounded-sm p-8 mb-6">
            <div className="flex gap-4 mb-6">
              {['Pennsylvania', 'Ohio', 'Virginia'].map(state => (
                <button
                  key={state}
                  data-testid={`state-tab-${state.toLowerCase()}`}
                  onClick={() => setSelectedState(state)}
                  className={`px-6 py-3 rounded-sm border transition-colors ${
                    selectedState === state
                      ? 'bg-primary text-text_light border-primary'
                      : 'bg-white text-text_dark border-borders hover:border-primary'
                  }`}
                >
                  {state}
                </button>
              ))}
            </div>

            {selectedRegistry && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl tracking-tight font-medium mb-3">{selectedRegistry.registry_name}</h3>
                  <p className="text-base leading-relaxed text-gray-700">{selectedRegistry.description}</p>
                </div>

                <div className="border-t border-borders pt-6">
                  <h4 className="text-xl font-medium mb-3 flex items-center gap-2">
                    <FileText size={24} className="text-accent" />
                    How It Works
                  </h4>
                  <p className="text-base leading-relaxed text-gray-700">{selectedRegistry.how_it_works}</p>
                </div>

                {selectedRegistry.registration_url && (
                  <div className="bg-accent/10 border border-accent p-6 rounded-sm">
                    <h4 className="text-xl font-medium mb-3 flex items-center gap-2">
                      <ArrowRight size={24} className="text-accent" />
                      Register Now
                    </h4>
                    <p className="text-base leading-relaxed mb-4">
                      Pennsylvania residents can register with the official state pesticide hypersensitivity registry:
                    </p>
                    <a
                      href={selectedRegistry.registration_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="pa-registry-link"
                      className="inline-flex items-center gap-2 bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                    >
                      Visit PA Department of Agriculture Registry
                      <ArrowRight size={20} />
                    </a>
                  </div>
                )}

                <div className="border-t border-borders pt-6">
                  <h4 className="text-xl font-medium mb-3 flex items-center gap-2">
                    <Warning size={24} className="text-red-600" />
                    Legal Requirements for Pest Control Operators
                  </h4>
                  <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">{selectedRegistry.legal_requirements}</p>
                </div>

                <div className="border-t border-borders pt-6">
                  <h4 className="text-xl font-medium mb-3 flex items-center gap-2">
                    <CheckCircle size={24} className="text-accent" />
                    Instructions for Operators
                  </h4>
                  <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">{selectedRegistry.operator_instructions}</p>
                </div>

                <div className="border-t border-borders pt-6">
                  <h4 className="text-xl font-medium mb-3 flex items-center gap-2">
                    <ShieldCheck size={24} className="text-accent" />
                    Your Rights as a Citizen
                  </h4>
                  <p className="text-base leading-relaxed text-gray-700 whitespace-pre-line">{selectedRegistry.citizen_rights}</p>
                </div>

                <div className="bg-gray-50 border border-borders p-6 rounded-sm">
                  <h4 className="text-xl font-medium mb-3">Contact Information</h4>
                  <div className="space-y-2 text-base leading-relaxed">
                    <p><strong>Department:</strong> {selectedRegistry.contact_info.department}</p>
                    <p><strong>Phone:</strong> {selectedRegistry.contact_info.phone}</p>
                    {selectedRegistry.contact_info.complaint_hotline && (
                      <p><strong>Complaint Hotline:</strong> {selectedRegistry.contact_info.complaint_hotline}</p>
                    )}
                    {selectedRegistry.contact_info.email && (
                      <p><strong>Email:</strong> {selectedRegistry.contact_info.email}</p>
                    )}
                    <p><strong>Website:</strong> <a href={selectedRegistry.contact_info.website} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">{selectedRegistry.contact_info.website}</a></p>
                    {selectedRegistry.contact_info.address && (
                      <p><strong>Address:</strong> {selectedRegistry.contact_info.address}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pesticide Ingredients Database */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Warning size={32} weight="duotone" className="text-accent" />
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium">Pesticide Ingredients Database</h2>
          </div>

          <div className="bg-white border border-borders rounded-sm p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  data-testid="ingredient-search"
                  placeholder="Search ingredients or products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <select
                data-testid="risk-level-filter"
                value={selectedRiskLevel}
                onChange={(e) => setSelectedRiskLevel(e.target.value)}
                className="px-4 py-3 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Risk Levels</option>
                <option value="low">Low Risk</option>
                <option value="moderate">Moderate Risk</option>
                <option value="high">High Risk</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : (
            <div className="grid gap-6">
              {filteredIngredients.map(ingredient => (
                <div key={ingredient.id} className="bg-white border border-borders rounded-sm p-6" data-testid={`ingredient-${ingredient.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl tracking-tight font-medium mb-2">{ingredient.ingredient_name}</h3>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-sm border text-xs uppercase tracking-[0.2em] ${riskLevelColors[ingredient.risk_level]}`}>
                          {ingredient.risk_level} Risk
                        </span>
                        <div className="flex items-center gap-2">
                          {ingredient.pet_safe ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <Dog size={18} /> Pet-Safe
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-sm">
                              <Dog size={18} /> Not Pet-Safe
                            </span>
                          )}
                          {ingredient.child_safe ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <Baby size={18} /> Child-Safe
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-sm">
                              <Baby size={18} /> Not Child-Safe
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2">Common Products</h4>
                      <div className="flex flex-wrap gap-2">
                        {ingredient.common_products.map((product, idx) => (
                          <span key={product} className="px-3 py-1 bg-gray-100 text-sm rounded-sm">{product}</span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                        <FirstAid size={18} className="text-red-600" />
                        Potential Symptoms
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-base leading-relaxed">
                        {ingredient.symptoms.map((symptom, idx) => (
                          <li key={symptom}>{symptom}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                        <ShieldCheck size={18} className="text-accent" />
                        Safety Precautions
                      </h4>
                      <ul className="list-disc list-inside space-y-1 text-base leading-relaxed">
                        {ingredient.precautions.map((precaution, idx) => (
                          <li key={precaution}>{precaution}</li>
                        ))}
                      </ul>
                    </div>

                    {ingredient.alternative_options && (
                      <div className="bg-green-50 border border-green-200 p-4 rounded-sm">
                        <h4 className="font-medium text-sm uppercase tracking-[0.2em] mb-2 text-green-800">
                          Safer Alternatives
                        </h4>
                        <p className="text-base leading-relaxed text-green-900">{ingredient.alternative_options}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Questions to Ask Your Pest Control Company */}
        <div className="mb-16">
          <div className="bg-white border border-borders rounded-sm p-8">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Questions to Ask Before Treatment</h2>
            <div className="space-y-4 text-base leading-relaxed">
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>What pesticide products will you be using?</strong> Request the product name, EPA registration number, and active ingredients.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>Can I see the label and Safety Data Sheet?</strong> Federal law requires companies to provide this information upon request.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>Do you offer lower-toxicity alternatives?</strong> Ask about Integrated Pest Management (IPM) and least-toxic options.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>How long should I keep children and pets away?</strong> Understand re-entry times and safety waiting periods.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>What precautions will you take to prevent drift?</strong> Especially important for outdoor applications near homes.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={24} className="text-accent flex-shrink-0 mt-1" />
                <p><strong>Have you checked the state hypersensitivity registry?</strong> In Pennsylvania, this is a legal requirement.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Official Resources */}
        <div className="mb-16">
          <div className="bg-white border border-borders rounded-sm p-8">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Official Safety Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-medium mb-3">EPA (Environmental Protection Agency)</h3>
                <ul className="space-y-2 text-base leading-relaxed">
                  <li><a href="https://www.epa.gov/pesticides" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Pesticide Information</a></li>
                  <li><a href="https://www.epa.gov/safepestcontrol" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Safe Pest Control</a></li>
                  <li><a href="https://www.epa.gov/pesticide-registration/pesticide-product-labels" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Product Label Database</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">CDC (Centers for Disease Control)</h3>
                <ul className="space-y-2 text-base leading-relaxed">
                  <li><a href="https://www.cdc.gov/pesticides/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Pesticide Safety</a></li>
                  <li><a href="https://www.atsdr.cdc.gov/substances/index.asp" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Chemical Substances Portal</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">NPMA (National Pest Management Association)</h3>
                <ul className="space-y-2 text-base leading-relaxed">
                  <li><a href="https://www.pestworld.org/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Consumer Resources</a></li>
                  <li><a href="https://www.npmapestworld.org/pest-control/ipm/" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">IPM Information</a></li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-medium mb-3">State Resources</h3>
                <ul className="space-y-2 text-base leading-relaxed">
                  <li><a href="https://www.agriculture.pa.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">PA Dept of Agriculture</a></li>
                  <li><a href="https://agri.ohio.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Ohio Dept of Agriculture</a></li>
                  <li><a href="https://www.vdacs.virginia.gov" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Virginia DACS</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Sensitivity Profile Checklist */}
        {user && (
          <div className="mb-16">
            <div className="bg-white border border-borders rounded-sm p-8">
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium mb-6">Personal Sensitivity Profile</h2>
              <p className="text-base leading-relaxed mb-6">
                Create a personal sensitivity profile that you can share with your pest control provider to ensure safe treatment options.
              </p>

              {!showProfileForm && !profile && (
                <button
                  data-testid="create-profile-btn"
                  onClick={() => setShowProfileForm(true)}
                  className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                >
                  Create Sensitivity Profile
                </button>
              )}

              {!showProfileForm && profile && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      {profile.has_asthma ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Asthma</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.has_allergies ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Allergies</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.has_chemical_sensitivity ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Chemical Sensitivity</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.has_skin_conditions ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Skin Conditions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.has_pets ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Have Pets {profile.pet_types.length > 0 && `(${profile.pet_types.join(', ')})`}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {profile.has_children ? <CheckCircle size={20} className="text-green-600" /> : <XCircle size={20} className="text-gray-400" />}
                      <span>Have Children {profile.children_ages.length > 0 && `(${profile.children_ages.join(', ')})`}</span>
                    </div>
                  </div>
                  {profile.specific_sensitivities.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-2">Specific Sensitivities:</h4>
                      <p className="text-base leading-relaxed">{profile.specific_sensitivities.join(', ')}</p>
                    </div>
                  )}
                  {profile.notes && (
                    <div>
                      <h4 className="font-medium mb-2">Additional Notes:</h4>
                      <p className="text-base leading-relaxed">{profile.notes}</p>
                    </div>
                  )}
                  <div className="flex gap-4">
                    <button
                      data-testid="edit-profile-btn"
                      onClick={() => setShowProfileForm(true)}
                      className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                    >
                      Edit Profile
                    </button>
                    <button
                      data-testid="print-profile-btn"
                      onClick={() => window.print()}
                      className="bg-accent text-white px-6 py-3 rounded-sm hover:bg-accent/90 transition-colors"
                    >
                      Print/Save Profile
                    </button>
                  </div>
                </div>
              )}

              {showProfileForm && (
                <form onSubmit={handleProfileSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_asthma" defaultChecked={profile?.has_asthma} className="w-5 h-5" data-testid="asthma-checkbox" />
                      <span>I have asthma</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_allergies" defaultChecked={profile?.has_allergies} className="w-5 h-5" />
                      <span>I have allergies</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_chemical_sensitivity" defaultChecked={profile?.has_chemical_sensitivity} className="w-5 h-5" />
                      <span>I have chemical sensitivities</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_skin_conditions" defaultChecked={profile?.has_skin_conditions} className="w-5 h-5" />
                      <span>I have skin conditions</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_pets" defaultChecked={profile?.has_pets} className="w-5 h-5" />
                      <span>I have pets</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" name="has_children" defaultChecked={profile?.has_children} className="w-5 h-5" />
                      <span>I have children</span>
                    </label>
                  </div>

                  <div>
                    <label className="block mb-2 font-medium">Pet Types (comma-separated)</label>
                    <input
                      type="text"
                      name="pet_types"
                      defaultValue={profile?.pet_types.join(', ')}
                      placeholder="e.g., Dogs, Cats, Birds"
                      className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-medium">Children Ages (comma-separated)</label>
                    <input
                      type="text"
                      name="children_ages"
                      defaultValue={profile?.children_ages.join(', ')}
                      placeholder="e.g., 3, 7, 12"
                      className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-medium">Specific Sensitivities (comma-separated)</label>
                    <input
                      type="text"
                      name="specific_sensitivities"
                      defaultValue={profile?.specific_sensitivities.join(', ')}
                      placeholder="e.g., Pyrethroids, Fragrances, Dust"
                      className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>

                  <div>
                    <label className="block mb-2 font-medium">Additional Notes</label>
                    <textarea
                      name="notes"
                      defaultValue={profile?.notes}
                      rows="4"
                      placeholder="Any additional information your pest control provider should know..."
                      className="w-full px-4 py-2 border border-borders rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    ></textarea>
                  </div>

                  <div className="flex gap-4">
                    <button
                      type="submit"
                      data-testid="save-profile-btn"
                      className="bg-primary text-text_light px-6 py-3 rounded-sm hover:bg-primary/90 transition-colors"
                    >
                      Save Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowProfileForm(false)}
                      className="bg-gray-200 text-text_dark px-6 py-3 rounded-sm hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
